import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bell, Download, BookOpen, Heart, HelpCircle, ArrowRight, 
  FileText, Users, ChevronRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const HERO_BG = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";
const ARTICLE_IMG = "https://images.pexels.com/photos/8522572/pexels-photo-8522572.jpeg";

export const MemberDashboard = () => {
  const { user, API } = useAuth();
  const [resources, setResources] = useState([]);
  const [welfare, setWelfare] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resResponse, welResponse, artResponse] = await Promise.all([
        fetch(`${API}/resources`),
        fetch(`${API}/welfare`),
        fetch(`${API}/articles`),
      ]);

      if (resResponse.ok) setResources(await resResponse.json());
      if (welResponse.ok) setWelfare(await welResponse.json());
      if (artResponse.ok) setArticles(await artResponse.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const quickActions = [
    { icon: Download, label: 'Downloads', subtext: `${resources.length} items`, path: '/resources' },
    { icon: BookOpen, label: 'New Resources', subtext: 'Browse all', path: '/resources' },
    { icon: Heart, label: 'Welfare Needs', subtext: `${welfare.length} active`, path: '/welfare', highlight: true },
    { icon: HelpCircle, label: 'Content Help', subtext: 'Support desk', path: '/contact' },
  ];

  const featuredArticle = articles[0] || {
    title: 'Weekly IRE Article',
    excerpt: 'Exploring Spiritual Growth in the Digital Age: Practical steps for modern believers.',
    image_url: ARTICLE_IMG,
  };

  return (
    <div 
      className="min-h-screen pb-20"
      style={{ 
        backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.8)), url(${HERO_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Header */}
      <header className="glass-card border-b-0 px-6 pt-8 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-xs font-medium text-primary uppercase tracking-widest mb-1 block">
              Assalamu Alaikum
            </span>
            <h1 className="text-2xl font-heading font-bold text-foreground">
              Welcome back, {user?.full_name?.split(' ')[0] || 'Member'}
            </h1>
          </div>
          <div className="relative">
            <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full glass-card" data-testid="notifications-btn">
              <Bell className="h-5 w-5" />
            </Button>
            <span className="absolute top-0 right-0 h-3 w-3 bg-primary border-2 border-background rounded-full" />
          </div>
        </div>
        
        {/* Arabic Calligraphy */}
        <div className="text-center py-2 overflow-hidden">
          <p className="font-amiri text-xl text-foreground/40 whitespace-nowrap">
            بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم
          </p>
        </div>
      </header>

      <div className="px-5 space-y-6 mt-4">
        {/* Featured Article Card */}
        <section data-testid="member-dashboard">
          <div className="glass-card rounded-2xl overflow-hidden">
            <div 
              className="h-48 w-full bg-cover bg-center relative"
              style={{ backgroundImage: `url(${featuredArticle.image_url || ARTICLE_IMG})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="bg-primary/80 backdrop-blur-md px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                  Featured Article
                </span>
              </div>
            </div>
            <div className="p-5">
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">
                {featuredArticle.title}
              </h2>
              <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                {featuredArticle.excerpt}
              </p>
              <Button className="w-full btn-primary" data-testid="read-article-btn">
                Read Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* Quick Access Grid */}
        <section>
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4 px-1">
            Quick Access
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.path}
                className="glass-card p-4 rounded-xl flex flex-col gap-3 hover:bg-white/10 transition-all group"
                data-testid={`quick-action-${index}`}
              >
                <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <action.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-sm text-foreground">{action.label}</p>
                  <p className={`text-xs ${action.highlight ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                    {action.subtext}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Recent Resources */}
        {resources.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4 px-1">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                Recent Resources
              </h3>
              <Link to="/resources" className="text-primary text-xs font-medium">
                View All
              </Link>
            </div>
            <div className="space-y-3">
              {resources.slice(0, 3).map((resource) => (
                <div 
                  key={resource.id}
                  className="glass-card p-4 rounded-xl flex items-center gap-4"
                >
                  <div className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-foreground truncate">{resource.title}</p>
                    <p className="text-xs text-muted-foreground">{resource.category}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Community Update */}
        <section className="glass-card p-5 rounded-xl border-dashed border-white/20">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-primary bg-muted flex items-center justify-center">
              <Users className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-foreground">Community Update</p>
              <p className="text-xs text-muted-foreground">
                Welcome to AKIT! Connect with fellow IRE teachers.
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-primary" />
          </div>
        </section>
      </div>
    </div>
  );
};
